import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import EmptyState from '../components/widgets/EmptyState';
import { fmtBRL, fmtPct, fmtBRLCompact } from '../lib/format';
import { Combine, PieChart, TrendingUp, Building2, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  PieChart as RPie, Pie,
} from 'recharts';

const TooltipDark = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-md p-3 shadow-xl text-[12px]">
      <div className="text-zinc-400 mb-1.5">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.color }} />
          <span className="text-zinc-300">{p.name}:</span>
          <span className="font-mono text-zinc-50">{fmtBRL(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Consolidado() {
  const { competence } = useApp();
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    (async () => {
      const k = await get('/dashboard/kpis', { competence });
      setKpis(k);
    })();
  }, [competence]);

  if (!kpis) return null;

  const totalProporcional = kpis.proportional?.lucro_liquido || 0;
  const totalLiquido = kpis.consolidated.lucro_liquido || 0;
  const totalReceita = kpis.consolidated.receita || 0;
  const hasData = (kpis.by_company || []).length > 0;
  const positiveCompanies = (kpis.by_company || []).filter((c) => c.lucro_proporcional > 0);

  if (!hasData) {
    return (
      <div className="fade-in">
        <TopBar
          title="Consolidado do Grupo CEAP BRASIL"
          subtitle="Visão consolidada considerando participação societária em cada empresa"
        />
        <div className="px-8 py-7">
          <SectionCard>
            <EmptyState
              icon={Combine}
              title="Sem dados nesta competência"
              description="Importe sua planilha de Fluxo de Caixa de cada empresa para ver a consolidação proporcional do grupo, ou troque o seletor de competência no topo."
              action={
                <Link to="/importar-fluxo-caixa">
                  <Button className="btn-gold gap-1.5" data-testid="btn-go-import">
                    <Upload size={14} /> Importar Fluxo de Caixa
                  </Button>
                </Link>
              }
              testid="consolidado-empty"
            />
          </SectionCard>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <TopBar
        title="Consolidado do Grupo CEAP BRASIL"
        subtitle="Visão consolidada considerando participação societária em cada empresa"
      />
      <div className="px-8 py-7 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard
            label="Receita Bruta Consolidada"
            value={kpis.consolidated.receita}
            accent="#D4AF37"
            icon={TrendingUp}
            testid="cons-receita"
            highlight
          />
          <KpiCard
            label="Receita Proporcional Grupo"
            value={kpis.proportional?.receita || 0}
            accent="#D4AF37"
            icon={Combine}
            testid="cons-receita-prop"
          />
          <KpiCard
            label="Lucro Líquido (100%)"
            value={kpis.consolidated.lucro_liquido}
            accent={totalLiquido >= 0 ? '#10B981' : '#EF4444'}
            icon={Building2}
            testid="cons-lucro"
          />
          <KpiCard
            label="Lucro Proporcional Grupo"
            value={totalProporcional}
            accent="#D4AF37"
            icon={PieChart}
            testid="cons-lucro-prop"
            highlight
          />
        </div>

        <SectionCard
          title="Memória de Cálculo - Participação Societária"
          subtitle="Como o lucro de cada empresa contribui para o resultado do grupo"
          padded={false}
          testid="participations-table"
        >
          <div className="overflow-x-auto">
            <table className="w-full ceap-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th className="text-right">Receita</th>
                  <th className="text-right">Custos</th>
                  <th className="text-right">Despesas</th>
                  <th className="text-right">Lucro Líq. (100%)</th>
                  <th className="text-right">% Grupo</th>
                  <th className="text-right">Lucro p/ Grupo</th>
                  <th className="text-right">% no Total</th>
                </tr>
              </thead>
              <tbody>
                {kpis.by_company.map((c) => {
                  const contrib = totalProporcional ? (c.lucro_proporcional / totalProporcional) * 100 : 0;
                  return (
                    <tr key={c.company_id} data-testid={`cons-row-${c.company_id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-7 rounded-sm" style={{ background: c.color }} />
                          <span className="text-zinc-100 font-medium">{c.name}</span>
                        </div>
                      </td>
                      <td className="text-right font-mono text-zinc-100">{fmtBRL(c.receita)}</td>
                      <td className="text-right font-mono text-amber-400">{fmtBRL(c.custos)}</td>
                      <td className="text-right font-mono text-red-400">{fmtBRL(c.despesas)}</td>
                      <td className={`text-right font-mono ${c.lucro_liquido >= 0 ? 'emerald-text' : 'red-text'}`}>
                        {fmtBRL(c.lucro_liquido)}
                      </td>
                      <td className="text-right font-mono gold-text">{c.ownership_pct.toFixed(2)}%</td>
                      <td className={`text-right font-mono font-semibold ${c.lucro_proporcional >= 0 ? 'gold-text' : 'red-text'}`}>
                        {fmtBRL(c.lucro_proporcional)}
                      </td>
                      <td className="text-right font-mono text-zinc-400">{fmtPct(contrib)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-zinc-900/50 font-semibold">
                  <td className="text-zinc-50">TOTAL CONSOLIDADO</td>
                  <td className="text-right font-mono gold-text">{fmtBRL(totalReceita)}</td>
                  <td className="text-right font-mono text-amber-400">{fmtBRL(kpis.consolidated.custos)}</td>
                  <td className="text-right font-mono text-red-400">{fmtBRL(kpis.consolidated.despesas)}</td>
                  <td className={`text-right font-mono ${totalLiquido >= 0 ? 'emerald-text' : 'red-text'}`}>
                    {fmtBRL(totalLiquido)}
                  </td>
                  <td className="text-right text-zinc-500">—</td>
                  <td className={`text-right font-mono ${totalProporcional >= 0 ? 'gold-text' : 'red-text'}`}>
                    {fmtBRL(totalProporcional)}
                  </td>
                  <td className="text-right text-zinc-500">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Composição do Lucro Proporcional" subtitle="Quanto cada empresa traz para o grupo">
            <div className="h-72">
              {positiveCompanies.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center px-6">
                  <div className="text-[13px] text-zinc-500 max-w-xs">
                    Nenhuma empresa com lucro proporcional positivo nesta competência. O gráfico só exibe empresas que estão contribuindo positivamente para o resultado consolidado.
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <RPie>
                    <Pie
                      data={positiveCompanies}
                      dataKey="lucro_proporcional"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      label={(d) => `${d.name.slice(0, 12)}`}
                      labelLine={false}
                    >
                      {positiveCompanies.map((c, i) => (
                        <Cell key={i} fill={c.color || '#D4AF37'} stroke="#09090B" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipDark />} />
                  </RPie>
                </ResponsiveContainer>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Comparativo Lucro 100% x Proporcional">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kpis.by_company} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="#52525B" fontSize={10} angle={-25} textAnchor="end" height={70} />
                  <YAxis stroke="#52525B" fontSize={10} tickFormatter={fmtBRLCompact} />
                  <Tooltip content={<TooltipDark />} />
                  <Bar dataKey="lucro_liquido" name="Lucro 100%" radius={[4, 4, 0, 0]}>
                    {kpis.by_company.map((c, i) => (
                      <Cell key={i} fill={c.lucro_liquido >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                  <Bar dataKey="lucro_proporcional" name="Proporcional Grupo" fill="#D4AF37" radius={[4, 4, 0, 0]} fillOpacity={0.85} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
